import { useState } from 'react';
import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { hasPermission } from '../../../core/permissions';
import { normalizePhone } from '../../../core/phone';
import { normalizeSoldierRole } from '../../../core/soldier-roles';
import { buildTimedStatus } from '../../../core/soldier-status';
import { createSoldier, updateSoldier } from '../../../db/fleet-store';
import {
  SOLDIER_AVAILABILITIES,
  SOLDIER_AVAILABILITY_LABELS,
  SOLDIER_ROLES,
  SOLDIER_ROLE_LABELS,
  TIMED_STATUS_KINDS,
  TIMED_STATUS_LABELS,
  WEIGHT_CATEGORIES,
  WEIGHT_CATEGORY_LABELS,
} from '../../../types/fleet';
import type {
  Soldier,
  SoldierAvailability,
  SoldierRole,
  TimedStatusKind,
  WeightCategory,
} from '../../../types/fleet';
import type { User } from '../../../types/user';

const fieldClass =
  'w-full h-10 rounded-xl border border-line bg-canvas px-3 text-sm focus:outline-none focus:border-brand-500 focus:bg-panel';

const DEFAULT_COMPANY = 'פלוגת שינוע 99';

/**
 * `MANAGE_SOLDIERS` is the gate for both dialogs in this file — and for the
 * quick-status sheet, which reuses this wall rather than writing a second one.
 */
export function PermissionWall({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="אין הרשאה" onClose={onClose}>
      <div className="text-center py-6">
        <div className="mx-auto mb-3 w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
          <Lock size={22} aria-hidden />
        </div>
        <p className="text-sm text-ink/70">
          נדרשת הרשאת ניהול חיילים (MANAGE_SOLDIERS) כדי להוסיף, לערוך או לשנות סטטוס.
        </p>
      </div>
    </Modal>
  );
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink/60 mb-1">{label}</span>
      {children}
    </label>
  );
}

/** ISO → the `YYYY-MM-DDTHH:mm` shape a `datetime-local` input expects. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ── Add / edit ───────────────────────────────────────────────────────

interface SoldierFormModalProps {
  user: User;
  /** `null` opens the form empty, for a new soldier. */
  soldier: Soldier | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

/**
 * Add / edit a soldier. The form asks for who they are, what they are licensed
 * to drive and where they stand — nothing else. The three-digit internal number
 * is a vehicle's and is not collected here, and the licence endorsements are
 * carried through untouched rather than edited.
 */
export function SoldierFormModal({ user, soldier, onClose, onSaved }: SoldierFormModalProps) {
  const canManage = hasPermission(user, 'MANAGE_SOLDIERS');

  const [fullName, setFullName] = useState(soldier?.fullName ?? '');
  const [personalNumber, setPersonalNumber] = useState(soldier?.personalNumber ?? '');
  // A record an older build filed under a job the roster no longer keeps opens
  // on נהג rather than on an empty select.
  const [role, setRole] = useState<SoldierRole>(normalizeSoldierRole(soldier?.role));
  const [company, setCompany] = useState(soldier?.company ?? DEFAULT_COMPANY);
  const [platoon, setPlatoon] = useState(soldier?.platoon ?? '');
  const [phone, setPhone] = useState(soldier?.phone ?? '');
  const [weight, setWeight] = useState<WeightCategory | ''>(soldier?.licenseWeightCategory ?? '');
  const [expiresAt, setExpiresAt] = useState((soldier?.licenseExpiresAt ?? '').slice(0, 10));
  const [availability, setAvailability] = useState<SoldierAvailability>(
    soldier?.availability ?? 'available',
  );
  const [notes, setNotes] = useState(soldier?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!canManage) return <PermissionWall onClose={onClose} />;

  const handleSubmit = async () => {
    setError(null);
    if (!fullName.trim() || !personalNumber.trim()) {
      setError('שם מלא ומספר אישי הם שדות חובה.');
      return;
    }

    const draft = {
      fullName: fullName.trim(),
      personalNumber: personalNumber.trim(),
      role,
      company: company.trim() || DEFAULT_COMPANY,
      platoon: platoon.trim(),
      phone: normalizePhone(phone),
      licenseWeightCategory: weight === '' ? null : weight,
      // Not edited here — an endorsement that arrived with the record stays.
      licensePermits: [...(soldier?.licensePermits ?? [])],
      licenseExpiresAt: expiresAt ? new Date(`${expiresAt}T00:00:00`).toISOString() : null,
      availability,
      temporaryStatus: soldier?.temporaryStatus ?? null,
      disallowedVehicleIds: soldier?.disallowedVehicleIds ?? [],
      notes: notes.trim() || undefined,
    };

    setSaving(true);
    try {
      if (soldier) await updateSoldier(user, soldier.id, draft);
      else await createSoldier(user, draft);
      await onSaved();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(
        /constraint/i.test(message) ? 'מספר אישי זה כבר קיים במערכת.' : `השמירה נכשלה — ${message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={soldier ? `עריכת ${soldier.fullName}` : 'הוספת חייל'}
      subtitle={soldier ? `מספר אישי ${soldier.personalNumber}` : 'הזינו את פרטי החייל החדש'}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'שומר…' : 'שמירה'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Labelled label="שם מלא">
          <input
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            className={fieldClass}
          />
        </Labelled>

        <div className="grid grid-cols-2 gap-3">
          <Labelled label="מספר אישי">
            <input
              value={personalNumber}
              onChange={(event) => setPersonalNumber(event.target.value)}
              inputMode="numeric"
              data-dir="ltr"
              className={fieldClass}
            />
          </Labelled>
          <Labelled label="טלפון">
            <input
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className={fieldClass}
            />
          </Labelled>
          <Labelled label="תפקיד">
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as SoldierRole)}
              className={fieldClass}
            >
              {SOLDIER_ROLES.map((option) => (
                <option key={option} value={option}>
                  {SOLDIER_ROLE_LABELS[option]}
                </option>
              ))}
            </select>
          </Labelled>
          <Labelled label="פלוגה">
            <input
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              className={fieldClass}
            />
          </Labelled>
          <Labelled label="מחלקה">
            <input
              value={platoon}
              onChange={(event) => setPlatoon(event.target.value)}
              className={fieldClass}
            />
          </Labelled>
          <Labelled label="דרגת רישיון">
            <select
              value={weight}
              onChange={(event) => setWeight(event.target.value as WeightCategory | '')}
              className={fieldClass}
            >
              <option value="">ללא רישיון צבאי</option>
              {WEIGHT_CATEGORIES.map((option) => (
                <option key={option} value={option}>
                  {WEIGHT_CATEGORY_LABELS[option]}
                </option>
              ))}
            </select>
          </Labelled>
          <Labelled label="תוקף הרישיון">
            <input
              type="date"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              className={fieldClass}
            />
          </Labelled>
        </div>

        <Labelled label="סטטוס קבוע">
          <select
            value={availability}
            onChange={(event) => setAvailability(event.target.value as SoldierAvailability)}
            className={fieldClass}
          >
            {SOLDIER_AVAILABILITIES.map((option) => (
              <option key={option} value={option}>
                {SOLDIER_AVAILABILITY_LABELS[option]}
              </option>
            ))}
          </select>
        </Labelled>

        <Labelled label="הערות מפקד">
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
            className="w-full rounded-xl border border-line bg-canvas p-3 text-sm focus:outline-none focus:border-brand-500 focus:bg-panel"
          />
        </Labelled>

        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

// ── Status change ────────────────────────────────────────────────────

interface SoldierStatusModalProps {
  user: User;
  soldier: Soldier;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

/**
 * Sets where a soldier is and, if it is known, when they are back — "בבית עד
 * יום שלישי 08:00". Clearing the kind drops the timed status and leaves the
 * permanent one.
 *
 * "עד מתי" is optional. A commander at the gate often knows that a soldier is
 * בגימלים and has no idea when they return; the dialog used to refuse to save
 * that at all, which left the roster showing the soldier as זמין rather than
 * record an honest status with no date on it. Left empty, the status is
 * written open-ended and runs until a commander changes it — the rule itself
 * lives in `buildTimedStatus()`.
 */
export function SoldierStatusModal({ user, soldier, onClose, onSaved }: SoldierStatusModalProps) {
  const canManage = hasPermission(user, 'MANAGE_SOLDIERS');

  const [availability, setAvailability] = useState<SoldierAvailability>(soldier.availability);
  const [kind, setKind] = useState<TimedStatusKind | ''>(soldier.temporaryStatus?.kind ?? '');
  const [until, setUntil] = useState(toLocalInput(soldier.temporaryStatus?.until));
  const [note, setNote] = useState(soldier.temporaryStatus?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!canManage) return <PermissionWall onClose={onClose} />;

  const handleSubmit = async () => {
    setError(null);
    setSaving(true);
    try {
      await updateSoldier(user, soldier.id, {
        availability,
        temporaryStatus: buildTimedStatus(kind, until, note),
      });
      await onSaved();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`השמירה נכשלה — ${message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`סטטוס — ${soldier.fullName}`}
      subtitle="סטטוס זמני מתפוגג לבדו במועד שנקבע — ובלי מועד, עד לשינוי ידני"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'שומר…' : 'עדכון סטטוס'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Labelled label="סטטוס קבוע">
          <select
            value={availability}
            onChange={(event) => setAvailability(event.target.value as SoldierAvailability)}
            className={fieldClass}
          >
            {SOLDIER_AVAILABILITIES.map((option) => (
              <option key={option} value={option}>
                {SOLDIER_AVAILABILITY_LABELS[option]}
              </option>
            ))}
          </select>
        </Labelled>

        <Labelled label="סטטוס זמני">
          <select
            value={kind}
            onChange={(event) => setKind(event.target.value as TimedStatusKind | '')}
            className={fieldClass}
          >
            <option value="">ללא סטטוס זמני</option>
            {TIMED_STATUS_KINDS.map((option) => (
              <option key={option} value={option}>
                {TIMED_STATUS_LABELS[option]}
              </option>
            ))}
          </select>
        </Labelled>

        {kind ? (
          <>
            <Labelled label="עד מתי (רשות)">
              <input
                type="datetime-local"
                data-status-until
                value={until}
                onChange={(event) => setUntil(event.target.value)}
                className={fieldClass}
              />
            </Labelled>
            <div className="flex items-start justify-between gap-3 -mt-1">
              <p className="text-xs text-ink/50">
                אפשר להשאיר ריק — הסטטוס יישאר בתוקף עד לשינוי ידני על ידי מפקד.
              </p>
              {until ? (
                <button
                  type="button"
                  data-clear-until
                  onClick={() => setUntil('')}
                  className="shrink-0 text-xs text-brand-700 hover:underline"
                >
                  ללא מועד סיום
                </button>
              ) : null}
            </div>
            <Labelled label="הערה">
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="למשל: אישור מפקד הפלוגה"
                className={fieldClass}
              />
            </Labelled>
          </>
        ) : null}

        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
